import { useFieldErrors } from '../hooks/useFieldErrors';
import { useState } from 'react';
import {
  Text, Spinner, makeStyles, tokens, Badge,
  Select, Field, Button, Checkbox,
} from '@fluentui/react-components';
import { DocumentPdf24Regular, ArrowDownload24Regular, Delete24Regular } from '@fluentui/react-icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchReportTypes, generateReport } from '../api/dashboard.api';
import { fetchTenantDocuments, fetchDocumentDownloadUrl, type DocumentItem } from '../api/document.api';
import {
  fetchScheduledReports, createScheduledReport, deleteScheduledReport,
  type ScheduledReport, type ReportFrequency,
} from '../api/scheduled-report.api';
import { DataTable, type DataTableColumn } from '../components/shared/DataTable';
import { useT } from '../i18n';
import { useLocaleSettings } from '../hooks/useLocaleSettings';
import type { DashboardReportType, Lote } from '../types/api.types';
import { fetchLotes } from '../api/lotes.api';
import { useRanchoStore } from '../store/rancho.store';
import { PageHeader } from '../components/shared/PageHeader';

// ── Styles ────────────────────────────────────────────────────────────────────

const useStyles = makeStyles({
  root:     { display: 'flex', flexDirection: 'column', gap: '24px' },
  grid:     { display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '20px' },
  card: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
    padding: '24px',
    display: 'flex', flexDirection: 'column', gap: '12px',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    transition: 'box-shadow 0.15s',
  },
  cardTitle:  { fontWeight: '700', fontSize: '16px' },
  cardDesc:   { fontSize: '13px', color: tokens.colorNeutralForeground3, flex: '1' },
  badge:      { alignSelf: 'flex-start' },
  resultBox: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
    padding: '20px',
    border: `1px solid ${tokens.colorBrandStroke1}`,
    display: 'flex', alignItems: 'center', gap: '16px',
  },
  errorBox: {
    backgroundColor: tokens.colorStatusDangerBackground1,
    borderRadius: tokens.borderRadiusMedium,
    padding: '16px',
    color: tokens.colorStatusDangerForeground1,
    fontSize: '13px',
  },
  section:      { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
  sectionTitle: { fontWeight: tokens.fontWeightSemibold, fontSize: tokens.fontSizeBase400 },
  scheduleForm: {
    display: 'flex', gap: tokens.spacingHorizontalM, alignItems: 'flex-end', flexWrap: 'wrap',
    padding: '16px', backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`, borderRadius: tokens.borderRadiusMedium,
  },
});

// ── Component ─────────────────────────────────────────────────────────────────

export function ReportsPage() {
  const { formatDate } = useLocaleSettings();
  const s = useStyles();
  const t = useT();
  const f = t.reports;
  const queryClient = useQueryClient();

  const [selectedLoteId, setSelectedLoteId] = useState<string>('');
  const [lastResult,     setLastResult]     = useState<{ url: string; filename: string } | null>(null);
  const [lastError,      setLastError]      = useState<string | null>(null);
  const [scheduleType,      setScheduleType]      = useState<string>('');
  const [scheduleFrequency, setScheduleFrequency] = useState<ReportFrequency>('WEEKLY');
  const [scheduleError,     setScheduleError]     = useState<string | null>(null);
  const fe = useFieldErrors();
  const [pushToTelegram,    setPushToTelegram]    = useState<boolean>(true);

  // Fetch report types from API
  const activeRanchoId = useRanchoStore(st => st.activeRanchoId);
  const { data: reportTypes = [], isLoading: loadingTypes } = useQuery({
    queryKey: ['report-types'],
    queryFn:  fetchReportTypes,
  });

  // Fetch lotes for CROP_SUMMARY_REPORT selector
  const { data: lotes = [] } = useQuery<Lote[]>({
    queryKey: ['lotes', activeRanchoId],
    queryFn:  () => fetchLotes(activeRanchoId),
  });

  // 5a — Reportes recientes (metadata persistida por reports.handler.ts al generar).
  const { data: recentReports = [], isLoading: loadingRecent } = useQuery({
    queryKey: ['tenant-documents', 'REPORT'],
    queryFn: () => fetchTenantDocuments('REPORT'),
  });

  // 5b — Reportes programados.
  const { data: scheduledReports = [], isLoading: loadingScheduled } = useQuery({
    queryKey: ['scheduled-reports'],
    queryFn: fetchScheduledReports,
  });

  const mutation = useMutation({
    mutationFn: generateReport,
    onSuccess: (data) => {
      setLastResult({ url: data.url, filename: data.filename });
      setLastError(null);
      queryClient.invalidateQueries({ queryKey: ['tenant-documents', 'REPORT'] });
    },
    onError: (err: Error) => {
      setLastError(err.message ?? f.generateError);
      setLastResult(null);
    },
  });

  const createScheduleMutation = useMutation({
    mutationFn: createScheduledReport,
    onSuccess: () => {
      setScheduleError(null);
      setScheduleType('');
      queryClient.invalidateQueries({ queryKey: ['scheduled-reports'] });
    },
    onError: (err: Error) => setScheduleError(err.message ?? f.scheduleCreateError),
  });

  const deleteScheduleMutation = useMutation({
    mutationFn: deleteScheduledReport,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['scheduled-reports'] }),
    onError: (err: Error) => setScheduleError(err.message ?? f.scheduleDeleteError),
  });

  const handleGenerate = (type: DashboardReportType) => {
    setLastResult(null);
    setLastError(null);
    mutation.mutate({
      type,
      loteId: type === 'CROP_SUMMARY_REPORT' ? selectedLoteId || undefined : undefined,
    });
  };

  async function handleDownloadRecent(doc: DocumentItem) {
    const url = await fetchDocumentDownloadUrl(doc.s3Key);
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  function handleCreateSchedule() {
    const req = t.common.validation.fieldRequired;
    const ok = fe.validate({
      scheduleType: !scheduleType && req,
      lote: scheduleType === 'CROP_SUMMARY_REPORT' && !selectedLoteId && req,
    });
    if (!ok || !scheduleType) return;
    setScheduleError(null);
    createScheduleMutation.mutate({
      type: scheduleType as DashboardReportType,
      frequency: scheduleFrequency,
      loteId: scheduleType === 'CROP_SUMMARY_REPORT' ? selectedLoteId || undefined : undefined,
      pushToTelegram: scheduleType === 'WEEKLY_FIELD_REPORT' ? pushToTelegram : undefined,
    });
  }

  function reportTypeLabel(type: DashboardReportType): string {
    return reportTypes.find(rt => rt.type === type)?.label ?? type;
  }

  const recentColumns: DataTableColumn<DocumentItem>[] = [
    { key: 'docType', header: f.colName, width: 1.6, render: d => reportTypeLabel(d.docType as DashboardReportType) },
    { key: 'createdAt', header: f.colDate, render: d => formatDate(d.createdAt.slice(0, 10)) },
    {
      key: 'actions', header: t.common.actions, align: 'right',
      render: d => (
        <Button size="small" icon={<ArrowDownload24Regular />} onClick={() => handleDownloadRecent(d)}>
          {f.downloadBtn}
        </Button>
      ),
    },
  ];

  const scheduledColumns: DataTableColumn<ScheduledReport>[] = [
    { key: 'type', header: f.colType, width: 1.6, render: sr => reportTypeLabel(sr.type) },
    { key: 'frequency', header: f.colFrequency, render: sr => f[`frequency${sr.frequency}` as 'frequencyWEEKLY' | 'frequencyMONTHLY'] },
    { key: 'nextRunAt', header: f.nextRun, render: sr => sr.nextRunAt },
    { key: 'lastRunAt', header: f.lastRun, render: sr => sr.lastRunAt ? formatDate(sr.lastRunAt.slice(0, 10)) : '—' },
    {
      key: 'actions', header: t.common.actions, align: 'right',
      render: sr => (
        <Button
          size="small"
          icon={<Delete24Regular />}
          onClick={() => deleteScheduleMutation.mutate(sr.scheduleId)}
          disabled={deleteScheduleMutation.isPending}
        >
          {f.deleteSchedule}
        </Button>
      ),
    },
  ];

  const generating = mutation.isPending;

  return (
    <div className={s.root}>
      <PageHeader title={f.title} subtitle={f.subtitle} />

      {loadingTypes && <Spinner size="small" label={f.loading} />}


      {/* Report cards */}
      <div className={s.grid}>
        {reportTypes.map((rt) => (
          <div key={rt.type} className={s.card}>
            <DocumentPdf24Regular style={{ color: tokens.colorBrandForeground1, fontSize: '28px' }} />

            <Badge appearance="tint" color="brand" className={s.badge}>
              {rt.requiresLoteId ? f.badgeLote : f.badgeTenant}
            </Badge>

            <Text className={s.cardTitle}>{rt.label}</Text>
            <Text className={s.cardDesc}>{rt.description}</Text>

            {rt.requiresLoteId && (
              <Field label={f.selectLote}>
                <Select
                  value={selectedLoteId}
                  onChange={(_, d) => setSelectedLoteId(d.value)}
                >
                  <option value="">{f.selectLotePlaceholder}</option>
                  {lotes.map((l: Lote) => (
                    <option key={l.loteId} value={l.loteId}>
                      {l.name || l.loteId} {l.cultivo ? `· ${l.cultivo}` : ''}
                    </option>
                  ))}
                </Select>
              </Field>
            )}

            <Button
              appearance="primary"
              icon={generating ? undefined : <ArrowDownload24Regular />}
              disabled={generating || (rt.requiresLoteId && !selectedLoteId)}
              onClick={() => handleGenerate(rt.type)}
            >
              {generating && mutation.variables?.type === rt.type
                ? f.generating
                : f.generateBtn}
            </Button>
          </div>
        ))}
      </div>

      {/* Result / error */}
      {lastResult && (
        <div className={s.resultBox}>
          <DocumentPdf24Regular style={{ fontSize: '32px', color: tokens.colorBrandForeground1 }} />
          <div style={{ flex: 1 }}>
            <Text weight="semibold">{f.readyTitle}</Text>
            <br />
            <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
              {lastResult.filename}  ·  {f.expiresNote}
            </Text>
          </div>
          <a href={lastResult.url} target="_blank" rel="noopener noreferrer" download={lastResult.filename}>
            <Button appearance="primary" icon={<ArrowDownload24Regular />}>
              {f.downloadBtn}
            </Button>
          </a>
        </div>
      )}

      {lastError && (
        <div className={s.errorBox}>
          ⚠ {lastError}
        </div>
      )}

      {/* 5a — Reportes recientes */}
      <div className={s.section}>
        <span className={s.sectionTitle}>{f.recentTitle}</span>
        <DataTable
          columns={recentColumns}
          rows={recentReports}
          rowKey={d => d.documentId}
          isLoading={loadingRecent}
          emptyMessage={f.noRecent}
        />
      </div>

      {/* 5b — Reportes programados */}
      <div className={s.section}>
        <span className={s.sectionTitle}>{f.scheduledTitle}</span>

        <div className={s.scheduleForm}>
          <Field label={f.scheduleTypeLabel} required {...fe.props('scheduleType')}>
            <Select value={scheduleType} onChange={(_, d) => { setScheduleType(d.value); fe.clear(); }}>
              <option value="">{f.selectLotePlaceholder}</option>
              {reportTypes.map(rt => (
                <option key={rt.type} value={rt.type}>{rt.label}</option>
              ))}
            </Select>
          </Field>

          <Field label={f.scheduleFrequencyLabel}>
            <Select value={scheduleFrequency} onChange={(_, d) => setScheduleFrequency(d.value as ReportFrequency)}>
              <option value="WEEKLY">{f.frequencyWEEKLY}</option>
              <option value="MONTHLY">{f.frequencyMONTHLY}</option>
            </Select>
          </Field>

          {scheduleType === 'CROP_SUMMARY_REPORT' && (
            <Field label={f.selectLote} required {...fe.props('lote')}>
              <Select value={selectedLoteId} onChange={(_, d) => { setSelectedLoteId(d.value); fe.clear('lote'); }}>
                <option value="">{f.selectLotePlaceholder}</option>
                {lotes.map((l: Lote) => (
                  <option key={l.loteId} value={l.loteId}>{l.name || l.loteId}</option>
                ))}
              </Select>
            </Field>
          )}

          {scheduleType === 'WEEKLY_FIELD_REPORT' && (
            <Field hint={f.pushToTelegramHint}>
              <Checkbox
                label={f.pushToTelegramLabel}
                checked={pushToTelegram}
                onChange={(_, d) => setPushToTelegram(!!d.checked)}
              />
            </Field>
          )}

          <Button
            appearance="primary"
            disabled={createScheduleMutation.isPending}
            onClick={handleCreateSchedule}
          >
            {f.scheduleCreateBtn}
          </Button>
        </div>

        {scheduleError && <div className={s.errorBox}>⚠ {scheduleError}</div>}

        <DataTable
          columns={scheduledColumns}
          rows={scheduledReports}
          rowKey={sr => sr.scheduleId}
          isLoading={loadingScheduled}
          emptyMessage={f.noScheduled}
        />
      </div>
    </div>
  );
}
